// @flow
import * as React from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import AdContainer from '../../components/Ads/AdContainer';
import classNames from 'classnames';
import type { Distance as DistanceType } from '../../types/Distance';
import facetTypes from '../../utils/search/facetTypes';
import { faSlidersH } from '@fortawesome/pro-regular-svg-icons/faSlidersH';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import isEqual from 'react-fast-compare';
import LocationShippingFacet from '../../components/Facets/LocationShippingFacet';
import MultiCheckFacet from '../../components/Facets/MultiCheckFacet';
import RangeFacet from '../../components/Facets/RangeFacet';
import SlideMenu from '@liveauctioneers/caterwaul-components/lib/SlideMenu/SlideMenu';
import { sortCountryFacets } from '../../utils/searchUtils';
import styles from './SearchFacets.module.scss';
import TaxonomyFacet from '../../components/Facets/TaxonomyFacet';

type Props = $ReadOnly<{|
    className?: string,
    clearAllParamsExceptCategories: Function,
    desktop?: boolean,
    distance: DistanceType,
    distanceSelected: Function,
    excludedFacets: any,
    facetOptionChecked: Function,
    facetOptionExcluded: Function,
    facets: any[],
    isLandingPage: boolean,
    pageName: string,
    pathname: string,
    queryParams: any,
    rangeEntered: Function,
    ranges: any,
    removeCountryAndDistance: Function,
    replaceCountry: Function,
    showLandingPageLinks: boolean,
    taxonomyAllSelected: Function,
    taxonomyParentSelected: Function,
    taxonomySelected: Function,
    totalItemsFound: number,
    whiteLabel?: boolean,
    width: number,
|}>;

const mapFacets = (facet, index, props, intl) => {
    const {
        facetOptionChecked,
        facetOptionExcluded,
        queryParams,
        rangeEntered,
        ranges,
        showLandingPageLinks,
        taxonomyAllSelected,
        taxonomyParentSelected,
        taxonomySelected,
        whiteLabel,
        width,
    } = props;

    const { id, categories, excludable, landingPageUrl, name, options, range } = facet;

    const { formatMessage } = intl;

    const title = formatMessage({ id: `search_facet_title_${id}` });
    if (options) {
        const showSearchHeader = name === 'Auction House' || name === 'Creator facet';
        const searchPlaceholder =
            name === 'Auction House'
                ? intl.formatMessage({ id: 'searchByHousePlaceholder' })
                : name === 'Creator facet'
                ? intl.formatMessage({ id: 'searchByCreatorPlaceholder' })
                : '';
        const loadPageWithFacetCollapsed = true;
        const emptyMessage =
            id === 'auctionHouse' ? (
                <FormattedMessage key="no_ah_found" id="search_no_auction_house_found" />
            ) : (
                <FormattedMessage key="no_creator_found" id="search_no_creator_found" />
            );
        return whiteLabel && name === 'Auction House' ? null : (
            <MultiCheckFacet
                checklist={options}
                emptyMessage={emptyMessage}
                excludable={excludable}
                facetOptionChecked={facetOptionChecked}
                facetOptionExcluded={facetOptionExcluded}
                facetId={id}
                searchPlaceholder={searchPlaceholder}
                showLandingPageLinks={showLandingPageLinks}
                loadPageWithFacetCollapsed={loadPageWithFacetCollapsed}
                key={index}
                queryParams={queryParams}
                showSearchHeader={showSearchHeader}
                title={title}
            />
        );
    } else if (range && (range.min || range.max)) {
        const min = ranges[id] && ranges[id].min !== '-1' ? range.min : 0;
        const max = ranges[id] && ranges[id].max !== '-1' ? range.max : 0;
        const loadPageWithFacetCollapsed = true;
        return (
            <RangeFacet
                facetId={id}
                key={index}
                loadPageWithFacetCollapsed={loadPageWithFacetCollapsed}
                max={max}
                min={min}
                rangeEntered={rangeEntered}
                title={title}
                width={width}
            />
        );
    } else if (categories) {
        const loadPageWithFacetCollapsed = name !== 'Category facet';
        return (
            <TaxonomyFacet
                category={categories}
                facetId={id}
                showLandingPageLinks={showLandingPageLinks}
                key={index}
                landingPageUrl={landingPageUrl}
                loadPageWithFacetCollapsed={loadPageWithFacetCollapsed}
                queryParams={queryParams}
                taxonomyAllSelected={taxonomyAllSelected}
                taxonomyParentSelected={taxonomyParentSelected}
                taxonomySelected={taxonomySelected}
                title={title}
            />
        );
    }

    return null;
};

const SearchFacets = (props: Props) => {
    const {
        clearAllParamsExceptCategories,
        desktop = false,
        distance,
        distanceSelected,
        facetOptionChecked,
        facets,
        isLandingPage,
        pageName,
        pathname,
        queryParams,
        removeCountryAndDistance,
        replaceCountry,
        totalItemsFound,
        whiteLabel,
    } = props;

    const classes = classNames(styles.unsetWidth, {
        [styles.landingFacets]: isLandingPage,
    });

    const intl = useIntl();

    // don't include country or shipping because they will be combined in the LocationShippingFacet later
    const facetElements = facets
        .filter(facet => facet.id !== facetTypes.COUNTRY_CODE_ID && facet.id !== facetTypes.SHIPPING)
        .map((facet, index) => mapFacets(facet, index, props, intl));

    const countryFacet = facets.filter(facet => facet.id === facetTypes.COUNTRY_CODE_ID)[0];
    const shippingFacet = facets.filter(facet => facet.id === facetTypes.SHIPPING)[0];
    const searchCountries = facets
        ? facets.filter(facet => facet.id === facetTypes.COUNTRY_CODE_ID)[0]?.options || []
        : [];
    const sortedAvailableCountries = sortCountryFacets(searchCountries);

    const locationShipping = (
        <LocationShippingFacet
            availableCountries={sortedAvailableCountries}
            countries={countryFacet?.options}
            countryFacet={countryFacet}
            distance={distance}
            distanceSelected={distanceSelected}
            facetOptionChecked={facetOptionChecked}
            loadPageWithFacetCollapsed
            key="countryCode"
            removeCountryAndDistance={removeCountryAndDistance}
            replaceCountry={replaceCountry}
            shippingFacet={shippingFacet}
            title={intl.formatMessage({ id: `search_facet_title_locationAndShipping` })}
        />
    );
    facetElements.length > 0 && facetElements.splice(1, 0, locationShipping);

    const showAdContainer = !whiteLabel && totalItemsFound > 0;

    const facetWrapper = (
        <div className={styles.facetsWrapper} preventclose="true">
            {facetElements}
            {showAdContainer && (
                <AdContainer
                    className={styles.ad}
                    isLandingPage={isLandingPage}
                    pageName={pageName}
                    pathname={pathname}
                    queryParams={queryParams}
                />
            )}
        </div>
    );

    return desktop ? (
        <div className={styles.desktopWrapper}>{facetWrapper}</div>
    ) : (
        <SlideMenu
            className={classes}
            icon={
                <div className={styles.mobileFilter}>
                    <FontAwesomeIcon icon={faSlidersH} className={styles.filterIcon} />
                </div>
            }
            footer={
                <div className={styles.mobileFooter} onClick={clearAllParamsExceptCategories}>
                    <span className={classNames('link___', styles.footerText)}>
                        <FormattedMessage id="searchPage.mobileFooter.clear" />
                    </span>
                </div>
            }
            header={
                <div className={styles.mobileTitle}>
                    <h2 className={styles.title}>
                        <FormattedMessage id="searchPage.mobileHeader.title" />
                    </h2>
                    <span className={styles.subtitle}>
                        <FormattedMessage
                            id="searchPage.mobileHeader.subtitle"
                            values={{
                                items: (
                                    <FormattedNumber
                                        maximumFractionDigits={0}
                                        minimumFractionDigits={0}
                                        value={totalItemsFound}
                                    />
                                ),
                            }}
                        />
                    </span>
                </div>
            }
            right
        >
            {facetWrapper}
        </SlideMenu>
    );
};

export default React.memo<Props>(SearchFacets, isEqual);
